import { useState, useRef, useCallback, useEffect } from 'react';

export default function AudioControls({ socket, streamKey, deviceStatus }) {
  const [isPTT, setIsPTT]             = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [micAllowed, setMicAllowed]   = useState(null);
  const streamRef   = useRef(null);
  const processorRef = useRef(null);

  const requestMic = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
      streamRef.current = stream;
      setMicAllowed(true);
      return stream;
    } catch {
      setMicAllowed(false);
      return null;
    }
  }, []);

  const startPTT = useCallback(async () => {
    if (!socket || deviceStatus !== 'ONLINE') return;
    const stream = streamRef.current || await requestMic();
    if (!stream) return;

    setIsPTT(true);
    const ctx      = new AudioContext();
    const source   = ctx.createMediaStreamSource(stream);
    const processor = ctx.createScriptProcessor(4096, 1, 1);
    processorRef.current = { ctx, processor };

    processor.onaudioprocess = (e) => {
      const data = e.inputBuffer.getChannelData(0);
      socket.emit('audio:push-to-talk', { streamKey, data: Array.from(data) });
    };

    source.connect(processor);
    processor.connect(ctx.destination);
  }, [socket, streamKey, deviceStatus, requestMic]);

  const stopPTT = useCallback(() => {
    setIsPTT(false);
    if (processorRef.current) {
      processorRef.current.processor.disconnect();
      processorRef.current.ctx.close();
      processorRef.current = null;
    }
  }, []);

  const toggleListen = useCallback(() => {
    setIsListening(l => !l);
  }, []);

  useEffect(() => {
    return () => {
      stopPTT();
      streamRef.current?.getTracks().forEach(t => t.stop());
    };
  }, [stopPTT]);

  const disabled = deviceStatus !== 'ONLINE';

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      background: 'var(--elvyra-surface-2)', borderRadius: 'var(--radius-md)',
      padding: 'var(--space-2) var(--space-3)', border: '1px solid var(--elvyra-border)',
    }}>
      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--elvyra-text-muted)', marginRight: 4 }}>Audio</span>

      {/* Push to Talk */}
      <button
        onMouseDown={startPTT}
        onMouseUp={stopPTT}
        onTouchStart={startPTT}
        onTouchEnd={stopPTT}
        disabled={disabled}
        title="Push-to-Talk (maintenir appuyé)"
        style={{
          display: 'flex', alignItems: 'center', gap: 4,
          padding: '6px 12px', borderRadius: 'var(--radius-sm)',
          background: isPTT ? 'var(--elvyra-danger)' : 'var(--elvyra-surface)',
          border: `1px solid ${isPTT ? 'var(--elvyra-danger)' : 'var(--elvyra-border)'}`,
          color: isPTT ? '#fff' : 'var(--elvyra-text-secondary)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontSize: 'var(--text-xs)', fontWeight: 600,
          opacity: disabled ? 0.4 : 1,
          transition: 'all 150ms',
          userSelect: 'none',
        }}>
        <span>{isPTT ? '🔴' : '🎙'}</span>
        {isPTT ? 'Parler...' : 'PTT'}
      </button>

      {/* Écoute */}
      <button
        onClick={toggleListen}
        disabled={disabled}
        title="Activer/désactiver l'écoute"
        style={{
          padding: '6px 10px', borderRadius: 'var(--radius-sm)',
          background: isListening ? 'rgba(0,212,255,0.1)' : 'var(--elvyra-surface)',
          border: `1px solid ${isListening ? 'var(--elvyra-accent)' : 'var(--elvyra-border)'}`,
          color: isListening ? 'var(--elvyra-accent)' : 'var(--elvyra-text-muted)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          fontSize: 16, opacity: disabled ? 0.4 : 1,
          transition: 'all 150ms',
        }}>
        {isListening ? '🔊' : '🔇'}
      </button>

      {micAllowed === false && (
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--elvyra-danger)' }}>Mic refusé</span>
      )}
      {disabled && (
        <span style={{ fontSize: 'var(--text-xs)', color: 'var(--elvyra-text-muted)' }}>Caméra hors ligne</span>
      )}
    </div>
  );
}
